import React, { useEffect, useState } from 'react';
import * as Tone from 'tone';
import type { Melody } from '../lib/types';
import { api } from '../lib/api';

interface MelodyNotesProps {
  melody: Melody;
  shouldPlay: boolean;
  onComplete?: () => void;
  isLoading?: boolean;
  melodyIndex: number;
}

const MelodyNotes: React.FC<MelodyNotesProps> = ({ 
  melody, 
  shouldPlay, 
  onComplete,
  isLoading = false,
  melodyIndex
}) => {
  const [currentNoteIndex, setCurrentNoteIndex] = useState<number | null>(null);
  const [synth, setSynth] = useState<Tone.Sampler | null>(null);
  // const [synth, setSynth] = useState<Tone.AMSynth | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Initialize synth
  useEffect(() => {
  // const freeverb = new Tone.Freeverb().toDestination();
  // freeverb.dampening = 10000;
    const newSynth = new Tone.Sampler({
      urls: {
        "C4": "C4.mp3",
        "D#4": "Ds4.mp3",
        "F#4": "Fs4.mp3",
        "A4": "A4.mp3",
      },
      baseUrl: "https://tonejs.github.io/audio/salamander/",
      onload: () => {
        console.log('Piano samples loaded');
        setIsLoaded(true);
      }
    }).toDestination();
  // const newSynth = new Tone.AMSynth().connect(freeverb).toDestination();
  // setIsLoaded(true);

    setSynth(newSynth);

    return () => {
      if (newSynth) {
        newSynth.dispose();
      }
      Tone.Transport.stop();
    };
  }, []);

  // Handle playback
  useEffect(() => {
    if (shouldPlay && synth && isLoaded) {
      console.log('Starting melody playback:', {
        melody,
        time: Tone.now()
      });

      let currentIndex = 0;
      let timeoutId: number | null = null;

      const playNextNote = () => {
        if (currentIndex >= melody.length) {
          console.log('Melody finished:', {
            melody,
            time: Tone.now()
          });
          // Mark melody as played
          api.markMelodyAsPlayed(melodyIndex).catch(error => {
            console.error('Error marking melody as played:', error);
          });
          setTimeout(() => {
            onComplete?.();
          }, 500);
          return;
        }

        const note = melody[currentIndex];
        const formattedNote = formatNote(note);
        console.log('Playing note:', {
          note: formattedNote,
          time: Tone.now(),
          melody
        });

        synth.triggerAttackRelease(formattedNote, "8n");
        setCurrentNoteIndex(currentIndex);

        // Schedule next note
        timeoutId = window.setTimeout(() => {
          setCurrentNoteIndex(null);
          currentIndex++;
          playNextNote();
        }, 500);
      };

      // Start playing
      playNextNote();

      return () => {
        console.log('Cleaning up melody playback:', {
          melody,
          time: Tone.now()
        });
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        setCurrentNoteIndex(null);
      };
    }
  }, [shouldPlay, melody, synth, onComplete, isLoaded, melodyIndex]);

  const formatNote = (note: string): string => {
    if (!note.match(/\d$/)) {
      return note + '4';
    }
    return note;
  };

  const playNote = async (note: string, index: number) => {
    if (!synth || !isLoaded) return;
    const formattedNote = formatNote(note);
    await Tone.start();
    setCurrentNoteIndex(index);
    synth.triggerAttackRelease(formattedNote, "8n");
    setTimeout(() => setCurrentNoteIndex(null), 500);
  };

  if (isLoading) {
    return (
      <section aria-label="Melody notes" className="flex flex-col items-center">
        <ul className="flex flex-wrap gap-2 mb-4 justify-center">
          {[...Array(8)].map((_, index) => (
            <li key={index}>
              <div className="h-8 w-12 bg-gray-700 rounded-full animate-pulse"></div>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section aria-label="Melody notes" className="flex flex-col items-center">
      {!isLoaded && (
        <p className="text-sm text-gray-400 mb-4">Loading piano samples...</p>
      )}
      <ul className="flex flex-wrap gap-2 mb-4 justify-center">
        {melody.map((note, index) => (
          <li key={index}>
            <button
              onClick={() => playNote(note, index)}
              disabled={!isLoaded}
              className={`px-3 py-1 rounded-full transition-all duration-200 ${
                currentNoteIndex === index
                  ? 'bg-blue-500 text-white scale-110 shadow-lg'
                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
              } ${!isLoaded ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label={`Play note ${note}`}
            >
              {formatNote(note)}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default MelodyNotes; 